import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams, useNavigate} from 'react-router-dom';
import moment from 'moment';
import {Card, Grid, Divider, Icon, IconButton, Tooltip, Box, CircularProgress} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {activityLogsApi} from 'api/activityLogsApi';
import {formatActionMessage} from 'utils/activityLogUtils';

const methodBadgeColor = method => {
  if (method === 'POST') return 'success';
  if (method === 'PATCH' || method === 'PUT') return 'info';
  if (method === 'DELETE') return 'error';
  return 'secondary';
};

const roleBadgeColor = role => (role === 'superAdmin' ? 'dark' : 'info');

const MetaRow = ({label, value}) => (
  <Grid item xs={12} sm={6} md={3}>
    <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
      {label}
    </MDTypography>
    <MDTypography variant="button" sx={{wordBreak: 'break-word'}}>
      {value ?? '—'}
    </MDTypography>
  </Grid>
);

const ActivityLogDetails = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const {user} = useSelector(state => state.auth);

  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLog = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await activityLogsApi.fetchLogById(user?.accessToken, id);
      setLog(data);
    } catch (err) {
      console.error('Error fetching activity log details:', err);
      setError('Failed to load activity log details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={5} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size="3rem" color="inherit" />
            <MDTypography variant="h6" mt={2}>
              Loading log details...
            </MDTypography>
          </Box>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error || !log) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={5} mb={3}>
          <Card sx={{padding: 4, textAlign: 'center'}}>
            <Icon sx={{fontSize: 48, color: 'error.main', mb: 2}}>error_outline</Icon>
            <MDTypography variant="h5" mb={2}>
              {error || 'Log not found'}
            </MDTypography>
            <MDButton variant="contained" onClick={() => navigate('/activity-logs')}>
              Back to Activity Logs
            </MDButton>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} sx={{display: 'flex'}}>
            <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Back">
                      <IconButton color="white" onClick={() => navigate('/activity-logs')}>
                        <Icon sx={{color: 'white'}}>arrow_back</Icon>
                      </IconButton>
                    </Tooltip>
                    <MDTypography variant="h6" color="white">
                      Activity Log Details
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox p={3}>
                  <Grid container spacing={3} mb={3}>
                    <MetaRow label="Name" value={log.adminName} />
                    <MetaRow label="Email" value={log.adminEmail} />
                    <Grid item xs={12} sm={6} md={3}>
                      <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                        Role
                      </MDTypography>
                      {log.adminRole ? (
                        <MDBadge
                          badgeContent={log.adminRole}
                          color={roleBadgeColor(log.adminRole)}
                          variant="gradient"
                          size="sm"
                        />
                      ) : (
                        <MDTypography variant="button">—</MDTypography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                        Method
                      </MDTypography>
                      {log.method ? (
                        <MDBadge
                          badgeContent={log.method}
                          color={methodBadgeColor(log.method)}
                          variant="gradient"
                          size="sm"
                        />
                      ) : (
                        <MDTypography variant="button">—</MDTypography>
                      )}
                    </Grid>
                    <MetaRow
                      label="Timestamp"
                      value={log.createdAt ? moment(log.createdAt).format('DD MMM YYYY, HH:mm:ss') : '—'}
                    />
                  </Grid>

                  <Divider />

                  <MDBox mt={3}>
                    <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
                      Action
                    </MDTypography>
                    <MDBox
                      p={2.5}
                      borderRadius="lg"
                      sx={{
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                      }}>
                      <MDTypography variant="h6" fontWeight="medium" color="dark">
                        {formatActionMessage(log)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Divider orientation="vertical" sx={{mx: 0}} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default ActivityLogDetails;
